import React, { useState } from "react";
import styled from "styled-components";
import API from "../../../util/api";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
`;

const Dialog = styled.div`
  font-family: Verdana, Arial, sans-serif;
  font-size: 1.1em;
  border: 1px solid #aaaaaa;
  background: #ffffff;
  color: #222222;
  border-radius: 4px;
  position: relative;
  padding: 0.2em;
  width: 480px;
  max-width: 95vw;
  outline: none;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eeeeee;
  padding: 0.4em 0.6em;
  border-bottom: 1px solid #cccccc;
  .title {
    font-weight: bold;
  }
  .close {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.2em;
    line-height: 1;
  }
`;

const Content = styled.div`
  padding: 1em 0.8em;
  font-size: 0.95em;
  line-height: 1.5;
`;

const Warning = styled.div`
  border: 1px solid #d9534f;
  background: #fff5f5;
  color: #a94442;
  padding: 8px 12px;
  border-radius: 3px;
  margin-top: 10px;
  font-size: 0.88em;
`;

const ResultBox = styled.div`
  border: 1px solid ${({ ok }) => (ok ? "#5cb85c" : "#d9534f")};
  background: ${({ ok }) => (ok ? "#f0fff0" : "#fff5f5")};
  color: ${({ ok }) => (ok ? "#2d6a2d" : "#a94442")};
  padding: 10px 12px;
  border-radius: 3px;
  margin-top: 10px;
  font-size: 0.9em;
`;

const ButtonPane = styled.div`
  padding: 0.5em;
  text-align: right;
  border-top: 1px solid #e0e0e0;
  button {
    margin-left: 0.5em;
    padding: 0.4em 1em;
    font-size: 0.9em;
    border: 1px solid #888;
    background: #f0f0f0;
    border-radius: 3px;
    cursor: pointer;
    &:hover:not(:disabled) {
      background: #e0e0e0;
    }
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  button.primary {
    background: #d9534f;
    border-color: #c9302c;
    color: #fff;
    &:hover:not(:disabled) {
      background: #c9302c;
    }
  }
  button.confirm {
    background: #5cb85c;
    border-color: #4cae4c;
    color: #fff;
    &:hover:not(:disabled) {
      background: #449d44;
    }
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// step: "confirm1" | "confirm2" | "loading" | "result"
export default function SendWelcomeEmailModal({ show, subscription, onClose }) {
  const [step, setStep] = useState("confirm1");
  const [result, setResult] = useState(null);

  if (!show) return null;

  const handleClose = () => {
    setStep("confirm1");
    setResult(null);
    onClose();
  };

  const handleContinue = () => setStep("confirm2");

  const handleConfirmSend = async () => {
    setStep("loading");
    try {
      const resp = await API.sendWelcomeEmails({
        customer_code: subscription.customer_code,
        customer_id: subscription.customer_id,
        invoice_id: subscription.invoice_id,
      });

      setResult({
        ok: resp?.status === "Success",
        message: resp?.message || "An unexpected error occurred.",
        queued: resp?.queued ?? null,
        skipped: resp?.skipped ?? null,
      });
    } catch (err) {
      setResult({
        ok: false,
        message: err?.message || "Request failed. Please try again.",
        queued: null,
        skipped: null,
      });
    }
    setStep("result");
  };

  // ── Step 1: first confirmation ────────────────────────────────────────────
  if (step === "confirm1") {
    return (
      <Overlay>
        <Dialog>
          <TitleBar>
            <span className="title">Send Welcome Emails</span>
            <button className="close" onClick={handleClose}>
              ✕
            </button>
          </TitleBar>
          <Content>
            <p>
              This will send welcome emails to{" "}
              <strong>all ACTIVE teachers</strong> in this subscription.
            </p>
            <p>
              Teachers will receive a welcome email with a link to set their
              password and login.
            </p>
          </Content>
          <ButtonPane>
            <button onClick={handleClose}>Cancel</button>
            <button className="primary" onClick={handleContinue}>
              Continue
            </button>
          </ButtonPane>
        </Dialog>
      </Overlay>
    );
  }

  // ── Step 2: critical confirmation ────────────────────────────────────────
  if (step === "confirm2") {
    return (
      <Overlay>
        <Dialog>
          <TitleBar>
            <span className="title">Are you absolutely sure?</span>
            <button className="close" onClick={handleClose}>
              ✕
            </button>
          </TitleBar>
          <Content>
            <p>
              Are you absolutely sure? This action{" "}
              <strong>cannot be undone</strong> and will trigger emails for{" "}
              <strong>all active teachers</strong> in this subscription.
            </p>
            <Warning>
              Emails will be queued immediately and sent during the next queue
              processing cycle. Teachers already waiting in the queue will not
              receive duplicate emails.
            </Warning>
          </Content>
          <ButtonPane>
            <button onClick={handleClose}>Cancel</button>
            <button className="confirm" onClick={handleConfirmSend}>
              Confirm &amp; Send
            </button>
          </ButtonPane>
        </Dialog>
      </Overlay>
    );
  }

  // ── Step 3: loading ───────────────────────────────────────────────────────
  if (step === "loading") {
    return (
      <Overlay>
        <Dialog>
          <TitleBar>
            <span className="title">Sending…</span>
          </TitleBar>
          <Content>
            <p>
              <Spinner />
              Queuing welcome emails, please wait…
            </p>
          </Content>
          <ButtonPane>
            <button disabled>Cancel</button>
            <button className="confirm" disabled>
              <Spinner />
              Confirm &amp; Send
            </button>
          </ButtonPane>
        </Dialog>
      </Overlay>
    );
  }

  // ── Step 4: result ────────────────────────────────────────────────────────
  return (
    <Overlay>
      <Dialog>
        <TitleBar>
          <span className="title">
            {result?.ok ? "Emails Queued" : "Error"}
          </span>
          <button className="close" onClick={handleClose}>
            ✕
          </button>
        </TitleBar>
        <Content>
          <ResultBox ok={result?.ok}>
            <p style={{ margin: 0 }}>{result?.message}</p>
            {result?.ok && result.queued !== null && (
              <p style={{ margin: "6px 0 0" }}>
                <strong>{result.queued}</strong> email
                {result.queued !== 1 ? "s" : ""} queued
                {result.skipped > 0 && (
                  <>
                    {" "}
                    — <strong>{result.skipped}</strong> already pending
                    (skipped)
                  </>
                )}
                .
              </p>
            )}
          </ResultBox>
        </Content>
        <ButtonPane>
          <button className="confirm" onClick={handleClose}>
            Close
          </button>
        </ButtonPane>
      </Dialog>
    </Overlay>
  );
}
